import { Box, Grid, pn, Stack, useElementBreakpoints } from "@compass/core";
import { CustomFilters, DatePicker, Select } from "components";
import { CustomEntityOption } from "components/common/CustomFilters/CustomFilter";
import { CustomFilterEntity, StaffServiceFilter as StaffServiceFilterModel, StaffServiceFilterResource } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const StaffServiceFilter = (props: {
  filter: StaffServiceFilterModel;
  setFilter: (filter: StaffServiceFilterModel) => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<StaffServiceFilterResource>({});
  const properties = resource.properties || [];

  const entityOptions: CustomEntityOption[] = [
    { EntityType: CustomFilterEntity.StaffService, EntityName: "Staff Service" },
  ];

  useEffect(() => {
    ApiService.staffServices.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const dateFromField = () => {
    const propertyName = pn<StaffServiceFilterModel>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateFrom}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          )
        }
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<StaffServiceFilterModel>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateTo}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          )
        }
      />
    );
  };

  const staffField = () => {
    const propertyName = pn<StaffServiceFilterModel>("staff");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={props.filter.staff}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.staff = value;
              draft.staffId = value?.id;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.staff(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StaffServiceFilterModel>("serviceTypeIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.serviceTypes?.filter((x) => props.filter.serviceTypeIds?.includes(x.id))}
        options={resource.serviceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceTypeIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="lg">
      <CustomFilters
        allowedEntityTypes={entityOptions}
        filters={props.filter.customFilters}
        setFilters={(filters) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.customFilters = filters;
            })
          )
        }
        properties={resource.customFilterProperties}
      />

      <Box>
        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {dateFromField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {dateToField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {staffField()}
          </Grid>

          <Grid item xs={largeFieldBreakpoints}>
            {serviceTypeField()}
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
